// src/components/projects/ProjectsGrid.jsx (or wherever it's located)
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import projectsData from '../../utils/projectsData'; // Adjust path if needed
import { CategoryFilterContainer, FilterButton } from '../ui/FilterControls'; // Assuming filter styles are separate
import { Section, SectionTitle, SectionHeader,Highlight } from '../ui/PageElements'; // Assuming base section elements
import ResponsiveImage from '../ui/ResponsiveImage';
import { arrowUpRightIcon } from '../ui/icons.js';


// Styled components
const ProjectsSection = styled(Section)` // Use base Section if desired
    padding: 100px 0;
    position: relative;
`;

// SectionHeader, SectionTitle can be imported or defined here

const ProjectsGridContainer = styled.div` // Renamed from ProjectsGrid to avoid conflict with component name
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 40px; // Increased gap slightly

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 30px;
    }
`;

const ProjectCardContainer = styled.div`
    position: relative;
    padding-top: 15px; /* Keep space for the featured badge */
`;

// --- MODIFIED ProjectCard ---
const ProjectCard = styled(Link)`
    border: 1px solid var(--grid-color);
    background-color: rgba(0, 0, 0, 0.5);
    /* REMOVED PADDING */
    position: relative;
    transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
    overflow: hidden;
    display: flex; /* Use flex column to stack image and content */
    flex-direction: column;
    text-decoration: none;
    height: 100%; /* Make cards fill container height */

    &:hover {
        transform: translateY(-10px);
        box-shadow: 0 10px 30px rgba(255, 51, 153, 0.2);
        border-color: var(--primary-color);
    }

    &:before { /* Keep the arrow icon */
        content: "";
        position: absolute;
        top: 10px;
        right: 10px;
        width: 30px;
        height: 30px;
        background-image: url("${arrowUpRightIcon}");
        background-size: contain;
        background-repeat: no-repeat;
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 2; /* Ensure arrow is above image */
    }

    &:hover:before {
        opacity: 1;
    }
`;

// --- MODIFIED ProjectImage ---
const ProjectImage = styled(ResponsiveImage)`
    width: 100%;
    height: 200px; /* Adjust fixed height as desired */
    object-fit: cover; /* Fill the area, cropping if needed */
    display: block; /* Remove potential extra space */
    /* REMOVED MARGIN-TOP */
    /* REMOVED BORDER - rely on card border */
    /* Removed hover effect specific to image border */

    img {
        width: 100%;
        height: 200px;
        object-fit: cover;
        display: block;
    }
`;

// --- NEW ProjectCardContent ---
const ProjectCardContent = styled.div`
    padding: 25px; /* Apply padding here */
    display: flex;
    flex-direction: column;
    flex-grow: 1; /* Allow content to take remaining space */
`;


const ProjectCategory = styled.div`
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--primary-color);
    margin-bottom: 10px;
`;

const ProjectTitle = styled.h3`
    font-size: 24px;
    margin-bottom: 15px;
    font-weight: 700;
    color: var(--text-color);
`;

const ProjectDescription = styled.p`
    font-size: 14px;
    color: var(--text-muted);
    margin-bottom: 20px;
    flex-grow: 1; /* Allow description to push tags down */
`;

const ProjectTags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: auto; /* Push tags to the bottom */
`;

const ProjectTag = styled.span`
    font-size: 11px;
    padding: 3px 8px;
    border: 1px solid var(--grid-color);
    position: relative;
    color: var(--text-muted);
    transition: border-color 0.3s ease;

    /* Update hover style if needed, this still works */
    ${ProjectCard}:hover & {
        border-color: var(--primary-color);
    }
`;

const FeaturedBadge = styled.div`
    position: absolute;
    top: 0;
    left: 20px;
    background-color: var(--primary-color);
    color: var(--background-color);
    font-size: 11px;
    padding: 5px 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 700;
    z-index: 1;
`;

const NoResultsMessage = styled.div`
    grid-column: 1 / -1;
    text-align: center;
    padding: 50px;
    border: 1px dashed var(--grid-color);
    font-size: 16px;
    color: var(--primary-color);
`;

// --- Main Component ---
const ProjectsGrid = () => { // Renamed component function
    const [activeCategory, setActiveCategory] = useState('ALL');

    // Custom sort for categories: OTHER should be last
    const allCategories = [...new Set(projectsData.map(project => project.category.toUpperCase()))];
    const otherCategory = 'OTHER';
    const sortedCategories = allCategories
        .filter(category => category !== otherCategory)
        .sort();
    if (allCategories.includes(otherCategory)) {
        sortedCategories.push(otherCategory);
    }
    const categories = ['ALL', ...sortedCategories];

    // Filter projects by category
    const filtered = activeCategory === 'ALL'
        ? projectsData
        : projectsData.filter(project => project.category.toUpperCase() === activeCategory);

    // Sort projects: featured first, then by priority descending, then by year descending
    const filteredProjects = filtered.slice().sort((a, b) => {
        // Featured projects first
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;

        // Then sort by priority descending (higher priority first, default 0)
        const priorityA = a.priority || 0;
        const priorityB = b.priority || 0;
        if (priorityA !== priorityB) return priorityB - priorityA;

        // Then sort by year descending
        return b.year - a.year;
    });

    return (
        <ProjectsSection>
            {/* Assumes SectionHeader/Title are handled by imported Section or defined above */}
            <SectionHeader style={{ textAlign: 'center', marginBottom: '60px' }}>
                <SectionTitle>Featured Work</SectionTitle>
            </SectionHeader>

            <CategoryFilterContainer> {/* Use imported or defined Filter container */}
                {categories.map(category => (
                    <FilterButton
                        key={category}
                        $active={activeCategory === category}
                        onClick={() => setActiveCategory(category)}
                        aria-pressed={activeCategory === category}
                    >
                        {category}
                    </FilterButton>
                ))}
            </CategoryFilterContainer>

            <ProjectsGridContainer> {/* Use renamed grid container */}
                {filteredProjects.length > 0 ? (
                    filteredProjects.map(project => (
                        <ProjectCardContainer key={project.id}>
                            {project.featured && <FeaturedBadge>Featured</FeaturedBadge>}
                            {/* --- MODIFIED CARD STRUCTURE --- */}
                            <ProjectCard
                                to={project.link || `/projects/${project.id}`}
                                aria-label={`View ${project.title} project`}
                            >
                                <ProjectImage
                                    // Use placeholder if imageSrc is missing
                                    src={project.imageSrc || '/api/placeholder/400/200'}
                                    alt={`${project.title} preview`} // Improved alt text
                                    linkToFullRes={false}
                                />
                                <ProjectCardContent>
                                    <ProjectCategory>{project.category}</ProjectCategory>
                                    <ProjectTitle>{project.title}</ProjectTitle>
                                    <ProjectDescription>{project.description}</ProjectDescription>
                                    <ProjectTags>
                                        {project.tags.map((tag, index) => (
                                            <ProjectTag key={index}>{tag}</ProjectTag>
                                        ))}
                                    </ProjectTags>
                                </ProjectCardContent>
                            </ProjectCard>
                        </ProjectCardContainer>
                    ))
                ) : (
                    <NoResultsMessage>
                        No projects found in this category. Check back later!
                    </NoResultsMessage>
                )}
            </ProjectsGridContainer>
        </ProjectsSection>
    );
};

export default ProjectsGrid; // Use correct component name export